import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Infinity as InfinityIcon, LogOut, Package, Hammer, Download, Loader2 } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "anaa_admin_token";

function errText(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail?.msg || String(detail);
}

const api = (token) =>
  axios.create({ baseURL: `${BASE}/api/admin`, headers: token ? { Authorization: `Bearer ${token}` } : {} });

export default function Owner() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(!!token);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("orders");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [selfOnly, setSelfOnly] = useState(false);
  const [brandFilter, setBrandFilter] = useState("all");

  const loadData = useCallback(async (tk) => {
    const [pr, or] = await Promise.all([
      api(tk).get("/products"),
      api(tk).get("/orders"),
    ]);
    setProducts(pr.data.products);
    setOrders(or.data.orders);
  }, []);

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    api(token).get("/me")
      .then(() => { setAuthed(true); return loadData(token); })
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setToken(""); setAuthed(false); })
      .finally(() => setChecking(false));
  }, [token, loadData]);

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api().post("/login", { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token); setAuthed(true);
      await loadData(data.token);
    } catch (err) {
      setError(errText(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(""); setAuthed(false); };

  const toggleMode = async (slug, next) => {
    setProducts((prev) => prev.map((p) => (p.slug === slug ? { ...p, fulfillment_mode: next } : p)));
    try { await api(token).post(`/products/${slug}/fulfillment`, { mode: next }); }
    catch (err) {
      setProducts((prev) => prev.map((p) => (p.slug === slug ? { ...p, fulfillment_mode: next === "self" ? "printify" : "self" } : p)));
    }
  };

  const saveOrder = async (order_id, status, tracking) => {
    try {
      await api(token).post(`/orders/${order_id}/status`, { status, tracking });
      setOrders((prev) => prev.map((o) => (o.order_id === order_id ? { ...o, self_fulfillment_status: status, tracking } : o)));
    } catch (err) { console.error("Order status save failed (keeping optimistic UI):", err); }
  };

  const resubmit = async (order_id) => {
    try {
      const { data } = await api(token).post(`/orders/${order_id}/resubmit`);
      setOrders((prev) => prev.map((o) => (o.order_id === order_id
        ? { ...o, fulfillment: data.fulfillment, printify_order_id: data.fulfillment?.printify_order_id }
        : o)));
    } catch (err) { alert(errText(err.response?.data?.detail) || "Retry failed"); }
  };

  const sendProduction = async (order_id) => {
    try {
      const { data } = await api(token).post(`/orders/${order_id}/send-to-production`);
      setOrders((prev) => prev.map((o) => (o.order_id === order_id
        ? { ...o, fulfillment: { ...(o.fulfillment || {}), production: data.production, status: data.production?.status === "in_production" ? "in_production" : (o.fulfillment || {}).status } }
        : o)));
    } catch (err) { alert(errText(err.response?.data?.detail) || "Send to production failed"); }
  };

  const refreshPrintify = async (order_id) => {
    try {
      const { data } = await api(token).get(`/orders/${order_id}/printify`);
      setOrders((prev) => prev.map((o) => (o.order_id === order_id ? { ...o, printify_live: data.printify } : o)));
    } catch (err) { /* best-effort */ }
  };

  if (checking) {
    return <div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="text-neon animate-spin" size={40} /></div>;
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4" data-testid="owner-login">
        <form onSubmit={doLogin} className="w-full max-w-sm bg-white/[0.03] border border-white/10 rounded-2xl p-8">
          <div className="flex items-center gap-2 text-neon text-xs font-bold uppercase tracking-[0.25em] mb-5">
            <InfinityIcon size={16} /> Owner Access
          </div>
          <h1 className="text-3xl font-black uppercase tracking-tight text-white mb-6">Sign in</h1>
          <input data-testid="owner-email" type="email" required placeholder="owner email" value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-black/40 border border-white/15 rounded-lg px-4 py-3 text-sm text-white placeholder:text-white/35 mb-3 focus:outline-none focus:border-neon" />
          <input data-testid="owner-password" type="password" required placeholder="password" value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-black/40 border border-white/15 rounded-lg px-4 py-3 text-sm text-white placeholder:text-white/35 mb-4 focus:outline-none focus:border-neon" />
          {error && <p data-testid="owner-login-error" className="text-trash text-sm mb-4">{error}</p>}
          <button data-testid="owner-login-submit" type="submit" disabled={busy}
            className="w-full bg-neon text-black font-bold uppercase tracking-widest text-sm py-3 rounded-full hover:brightness-95 transition disabled:opacity-60">
            {busy ? "..." : "Enter dashboard"}
          </button>
        </form>
      </div>
    );
  }

  const brands = ["all", ...Array.from(new Set(products.map((p) => p.brandId)))];
  const shownProducts = brandFilter === "all" ? products : products.filter((p) => p.brandId === brandFilter);
  const shownOrders = selfOnly ? orders.filter((o) => o.has_self_items) : orders;
  const selfCount = products.filter((p) => p.fulfillment_mode === "self").length;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="owner-dashboard">
      <header className="border-b border-white/10 sticky top-0 bg-black/90 backdrop-blur z-10">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-neon font-black uppercase tracking-widest text-sm">
            <InfinityIcon size={18} /> Owner Dashboard
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="tab-orders" onClick={() => setTab("orders")}
              className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest ${tab === "orders" ? "bg-neon text-black" : "text-white/70 hover:text-white"}`}>Orders</button>
            <button data-testid="tab-products" onClick={() => setTab("products")}
              className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest ${tab === "products" ? "bg-neon text-black" : "text-white/70 hover:text-white"}`}>Products</button>
            <button data-testid="owner-logout" onClick={logout} className="ml-2 text-white/50 hover:text-trash" title="Log out"><LogOut size={18} /></button>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-4 md:px-8 py-8">
        {tab === "products" && (
          <section>
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <h2 className="text-2xl font-black uppercase tracking-tight">Fulfillment per product</h2>
              <span className="text-sm text-white/50">{selfCount} set to “make myself” · {products.length - selfCount} on Printify</span>
              <div className="ml-auto flex flex-wrap gap-2">
                {brands.map((b) => (
                  <button key={b} onClick={() => setBrandFilter(b)}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-widest border ${brandFilter === b ? "bg-neon text-black border-neon" : "border-white/15 text-white/60 hover:text-white"}`}>{b}</button>
                ))}
              </div>
            </div>
            <div className="grid gap-3">
              {shownProducts.map((p) => {
                const self = p.fulfillment_mode === "self";
                return (
                  <div key={p.slug} data-testid={`product-row-${p.slug}`} className="flex items-center gap-4 bg-white/[0.03] border border-white/10 rounded-xl p-3">
                    <img src={`${BASE}${p.image}`} alt={p.name} className="w-14 h-14 rounded-lg object-cover bg-black/40" />
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-sm truncate">{p.name}</p>
                      <p className="text-xs text-white/45">{p.brand} · {p.category}{p.printify_mapped ? " · Printify ready" : ""}</p>
                    </div>
                    {p.printFile && (
                      <a href={`${BASE}${p.printFile}`} download target="_blank" rel="noreferrer"
                        data-testid={`download-${p.slug}`}
                        className="hidden sm:inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-neon"><Download size={14} /> Print file</a>
                    )}
                    <button data-testid={`toggle-${p.slug}`} onClick={() => toggleMode(p.slug, self ? "printify" : "self")}
                      className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[11px] font-bold uppercase tracking-widest border transition ${self ? "bg-neon/15 border-neon text-neon" : "bg-white/5 border-white/15 text-white/70"}`}>
                      {self ? <><Hammer size={13} /> I make it</> : <><Package size={13} /> Printify</>}
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {tab === "orders" && (
          <section>
            <div className="flex items-center gap-3 mb-6">
              <h2 className="text-2xl font-black uppercase tracking-tight">Orders</h2>
              <label className="ml-auto flex items-center gap-2 text-sm text-white/60 cursor-pointer">
                <input type="checkbox" checked={selfOnly} onChange={(e) => setSelfOnly(e.target.checked)} data-testid="self-only-toggle" />
                Self-fulfill only
              </label>
            </div>
            {shownOrders.length === 0 ? (
              <p className="text-white/50 py-16 text-center">No orders yet.</p>
            ) : (
              <div className="grid gap-4">
                {shownOrders.map((o) => (
                  <OrderCard key={o.order_id} order={o} onSave={saveOrder} onResubmit={resubmit} onSendProduction={sendProduction} onRefresh={refreshPrintify} />
                ))}
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}

function FulfillBadge({ status }) {
  const map = {
    in_production: "bg-neon/15 text-neon",
    submitted: "bg-blue-500/15 text-blue-300",
    self_fulfillment: "bg-white/10 text-white/60",
    pending_mapping: "bg-yellow-500/15 text-yellow-300",
    pending: "bg-white/10 text-white/50",
    error: "bg-trash/20 text-trash",
    not_configured: "bg-white/10 text-white/50",
  };
  const cls = map[status] || "bg-white/10 text-white/60";
  return <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${cls}`}>{(status || "pending").replace(/_/g, " ")}</span>;
}

function OrderCard({ order, onSave, onResubmit, onSendProduction, onRefresh }) {
  const [status, setStatus] = useState(order.self_fulfillment_status || "new");
  const [tracking, setTracking] = useState(order.tracking || "");
  const c = order.customer || {};
  const addr = [c.address1, c.city, c.region, c.zip, c.country].filter(Boolean).join(", ");
  const ff = order.fulfillment || {};
  const hasPF = (order.items || []).some((it) => it.fulfillment !== "self");
  const live = order.printify_live;
  return (
    <div data-testid={`order-${order.order_id}`} className="bg-white/[0.03] border border-white/10 rounded-xl p-5">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <span className="font-bold text-neon">{order.order_id}</span>
        <span className="text-xs text-white/45">${Number(order.amount).toFixed(2)} · {order.payment_status}</span>
        {order.has_self_items && <span className="text-[10px] font-bold uppercase tracking-widest bg-neon/15 text-neon px-2 py-0.5 rounded-full">Make & ship</span>}
      </div>
      <p className="text-sm text-white/70">{c.name} — {c.email}</p>
      {addr && <p className="text-sm text-white/50">{addr}</p>}
      <div className="mt-3 divide-y divide-white/5 border-y border-white/5">
        {(order.items || []).map((it, i) => (
          <div key={`${it.slug}-${it.size}-${i}`} className="flex items-center gap-3 py-2">
            <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-white/5 text-white/60">{it.fulfillment === "self" ? "MINE" : "PF"}</span>
            <span className="text-sm flex-1">{it.name} · {it.size} × {it.qty}</span>
            {it.fulfillment === "self" && it.printFile && (
              <a href={`${process.env.REACT_APP_BACKEND_URL}${it.printFile}`} download target="_blank" rel="noreferrer"
                data-testid={`order-download-${order.order_id}-${i}`}
                className="inline-flex items-center gap-1.5 text-xs text-neon hover:underline"><Download size={13} /> print file</a>
            )}
          </div>
        ))}
      </div>
      {hasPF && (
        <div className="mt-3 rounded-lg border border-white/10 bg-black/30 p-3" data-testid={`pf-${order.order_id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest text-white/40">Printify</span>
            <FulfillBadge status={ff.status} />
            {ff.printify_order_id && <span className="text-[11px] text-white/40">#{ff.printify_order_id}</span>}
          </div>
          {ff.detail && <p className="mt-1 text-xs text-trash" data-testid={`pf-error-${order.order_id}`}>{ff.detail}</p>}
          {ff.production_error && <p className="mt-1 text-xs text-trash">Production: {ff.production_error}</p>}
          {live && !live.error && (
            <div className="mt-2 text-xs text-white/70">
              <span className="capitalize">{(live.status || "").replace(/[-_]/g, " ") || "—"}</span>
              {(live.shipments || []).map((s, i) => (
                <div key={i}>{(s.carrier || "").toUpperCase()} · {s.tracking_number || "pending"}{s.tracking_url && <> — <a className="text-neon hover:underline" href={s.tracking_url} target="_blank" rel="noreferrer">track</a></>}</div>
              ))}
            </div>
          )}
          <div className="mt-2 flex flex-wrap gap-2">
            {!ff.printify_order_id && ["error", "pending_mapping", "not_configured"].includes(ff.status) && (
              <button onClick={() => onResubmit(order.order_id)} data-testid={`resubmit-${order.order_id}`} className="text-[11px] font-bold uppercase tracking-widest border border-white/15 text-white/70 hover:text-neon hover:border-neon px-3 py-1.5 rounded-full transition">Retry submission</button>
            )}
            {ff.printify_order_id && ff.status !== "in_production" && (
              <button onClick={() => onSendProduction(order.order_id)} data-testid={`sendprod-${order.order_id}`} className="text-[11px] font-bold uppercase tracking-widest border border-white/15 text-white/70 hover:text-neon hover:border-neon px-3 py-1.5 rounded-full transition">Send to production</button>
            )}
            {ff.printify_order_id && (
              <button onClick={() => onRefresh(order.order_id)} data-testid={`refresh-${order.order_id}`} className="text-[11px] font-bold uppercase tracking-widest border border-white/15 text-white/70 hover:text-neon hover:border-neon px-3 py-1.5 rounded-full transition">Refresh status</button>
            )}
          </div>
        </div>
      )}
      {order.has_self_items && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid={`status-${order.order_id}`}
            className="bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm">
            <option value="new">New</option>
            <option value="made">Made</option>
            <option value="shipped">Shipped</option>
          </select>
          <input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="tracking #"
            data-testid={`tracking-${order.order_id}`}
            className="bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm flex-1 min-w-[140px] placeholder:text-white/30" />
          <button onClick={() => onSave(order.order_id, status, tracking)} data-testid={`save-${order.order_id}`}
            className="bg-neon text-black font-bold uppercase tracking-widest text-xs px-4 py-2 rounded-full">Save</button>
        </div>
      )}
    </div>
  );
}
